import { createQuery } from '@tanstack/solid-query';
import { ArrowLeft, CalendarDays, Trophy, Users } from 'lucide-solid';
import { type Component, For, Show, createSignal } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { useCan } from '~/shared/access/useCan';
import { type ClubChatRoomDto, chatApi } from '~/shared/api/chat-api';
import { ChatRoomKind, ChatRoomRole } from '~/shared/api/generated';
import { queryKeys } from '~/shared/api/query-keys';
import { clubDateOf, formatClubTime, formatInClubZone } from '~/shared/lib/clubZone';

const fmt = (iso: string | null | undefined, zone: string): string => {
  if (!iso) return '';
  return clubDateOf(iso, zone) === clubDateOf(new Date().toISOString(), zone)
    ? formatClubTime(iso, zone)
    : formatInClubZone(iso, zone, { day: 'numeric', month: 'short' });
};

const icons = { [ChatRoomKind.TOURNAMENT]: Trophy, [ChatRoomKind.OPEN_MATCH]: Users, [ChatRoomKind.GROUP_SESSION]: CalendarDays };

export const RoomsPane: Component<{ zone: string; enabled: boolean }> = (props) => {
  const canRespond = useCan('/clients', 'chat');
  const [open, setOpen] = createSignal<ClubChatRoomDto | null>(null);

  const rooms = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.chat.rooms(),
    queryFn: ({ signal }) => chatApi.listRooms({ signal }),
    enabled: props.enabled && canRespond(),
    staleTime: 10_000,
  }));

  const messages = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.chat.roomMessages(open()?.kind ?? 'none', open()?.subjectId ?? 'none'),
    queryFn: ({ signal }) => chatApi.roomMessages(open()!.kind, open()!.subjectId, 50, { signal }),
    enabled: props.enabled && canRespond() && !!open(),
  }));

  return (
    <Show
      when={open()}
      fallback={
        <div class="flex-1 overflow-y-auto">
          <Show
            when={(rooms.data?.length ?? 0) > 0}
            fallback={
              <p class="text-[13px] text-tertiary p-6 text-center">
                {rooms.isLoading ? 'Загрузка…' : 'Бесед пока нет'}
              </p>
            }
          >
            <For each={rooms.data}>
              {(r) => (
                <button
                  type="button"
                  class="press w-full text-left flex items-start gap-3 px-4 py-3 border-b border-hairline hover:bg-white/4"
                  onClick={() => setOpen(r)}
                >
                  <span class="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-white/8 flex items-center justify-center text-accent">
                    <Dynamic component={icons[r.kind as keyof typeof icons] ?? Trophy} class="h-4 w-4" />
                  </span>
                  <span class="flex-1 min-w-0">
                    <span class="flex items-center gap-2">
                      <span class="text-[13px] font-semibold truncate flex-1">
                        {r.title}
                        <span class="font-normal text-tertiary">
                          {r.coachName ? ` · тренер ${r.coachName}` : ''} · участников {r.participantCount}
                        </span>
                      </span>
                      <span class="text-[11px] text-tertiary shrink-0">
                        {fmt(r.lastMessageAt, props.zone)}
                      </span>
                    </span>
                    <span class="block text-[12px] text-tertiary truncate mt-0.5">
                      {r.lastMessagePreview ?? 'Нет сообщений'}
                    </span>
                  </span>
                </button>
              )}
            </For>
          </Show>
        </div>
      }
    >
      <div class="flex items-center gap-2 px-4 py-2 border-b border-hairline">
        <button
          type="button"
          class="press h-8 w-8 -ml-1 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
          aria-label="К беседам"
          onClick={() => setOpen(null)}
        >
          <ArrowLeft class="h-[18px] w-[18px]" />
        </button>
        <div class="flex-1 min-w-0">
          <p class="text-[14px] font-semibold truncate">{open()?.title}</p>
          <p class="text-[11px] text-tertiary">
            {open()?.coachName ? `Тренер ${open()?.coachName} · ` : ''}только чтение
          </p>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        <Show
          when={(messages.data?.length ?? 0) > 0}
          fallback={
            <p class="text-[13px] text-tertiary text-center p-6">
              {messages.isLoading ? 'Загрузка…' : 'В беседе пока не писали'}
            </p>
          }
        >
          <For each={messages.data}>
            {(m) => (
              <div class="rounded-md bg-white/4 border border-hairline px-3 py-2">
                <div class="flex items-center gap-2 text-[11px] text-tertiary">
                  <span class="font-semibold text-secondary">{m.senderName ?? 'Участник'}</span>
                  <Show when={m.senderRole === ChatRoomRole.COACH}>
                    <span class="rounded-full bg-accent/15 text-accent px-1.5 py-[1px]">
                      Тренер
                    </span>
                  </Show>
                  <span class="ml-auto">{fmt(m.createdAt, props.zone)}</span>
                </div>
                <p class="mt-1 text-[13px] text-primary whitespace-pre-wrap break-words">
                  {m.body}
                </p>
              </div>
            )}
          </For>
        </Show>
      </div>
      <p class="border-t border-hairline p-3 text-[12px] text-tertiary text-center">
        Беседу клуб только читает — пишут её участники в приложении.
      </p>
    </Show>
  );
};
